import React, { useState } from "react";
import classNames from "classnames";
import { DateTime } from "luxon";
import { UseCases } from "@planzone/core";
import { useAssignTag, useListEdition } from "@planzone/view-models";
import { Assignable, Removable, list as listMock } from "@planzone/dto";

import { ActivityWatcher } from "@common/activityWatcher";
import { useToggle } from "@common/treeToggle";
import { SecondaryCriteriaForm } from "@common/secondaryCriteriaForm";
import { useModal } from "@root/client/modal";
import { CommentsCount } from "../commentsCount";
import { AttachmentsCount } from "../attachmentsCount";
import { Props } from "./types";
import { ListToggleArrow } from "@common/listToggleArrow";
import { SecondaryCriteria } from "../secondaryCriteria";
import { useProjectCtx } from "../projectCtx";
import { Tags } from "../tags";
import { Context } from "../context";
import { ContextualMenu } from "./contextualMenu";
import { DateForm } from "./dateForm";
import styles from "./styles.scss";

function List({
  list,
  workLoad,
  meta,
  parentList,
  addIn,
  addAfter,
  minDate,
  maxDate,
}: Props) {
  const realList = { ...listMock, ...list };
  const { isOpen, toggle } = useToggle(
    `${workLoad.uuid}:${list.uuid}`,
    list.depth + 1,
    true,
  );
  const { pushView } = useModal();
  const { project, template } = useProjectCtx();
  const projectTimezone = project.timeZone || DateTime.local().zoneName;
  const { hoveredElement } = React.useContext(Context);
  const { assignTag } = useAssignTag();
  const { isRequestPending, editList } = useListEdition();
  const [showContextualMenu, setShowContextualMenu] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  ActivityWatcher.useActivityWatcher(ref, {
    focus: () => setShowContextualMenu(!showContextualMenu),
    blur: () => setShowContextualMenu(false),
  });

  return (
    <div
      className={classNames(styles.list, {
        [styles.hovered]: !!hoveredElement && hoveredElement.uuid === list.uuid,
      })}
    >
      <SecondaryCriteria project={project}>
        {() => ({
          main: (
            <div className={styles.main}>
              <div className={styles.iconBox} onClick={toggle}>
                <ListToggleArrow isOpen={isOpen} />
              </div>
              <h3
                className={styles.name}
                onClick={() =>
                  pushView({
                    key: UseCases.retrieveList,
                    data: { listId: list.uuid },
                  })
                }
              >
                {list.name}{" "}
                <span className={styles.elementsNbr}>{meta.elementCount}</span>
              </h3>
            </div>
          ),
          criteria: template.reduce(
            (acc, criteria) => ({
              ...acc,
              [criteria.uuid]: (
                <SecondaryCriteriaForm
                  criteria={criteria}
                  value={
                    list.attributes ? list.attributes[criteria.uuid] || "" : ""
                  }
                  projectTimezone={projectTimezone}
                  disabled={isRequestPending}
                  submit={(value) =>
                    editList({
                      list: realList,
                      nextList: {
                        ...realList,
                        attributes: {
                          ...list.attributes,
                          [criteria.uuid]: value,
                        },
                      },
                    })
                  }
                />
              ),
            }),
            {
              tags: (
                <p className={styles.tags}>
                  <Tags
                    tags={list.tags}
                    project={project}
                    removable={Removable.LIST}
                    uuid={list.uuid}
                    add={(tag) =>
                      assignTag({
                        tag,
                        assignable: Assignable.LIST,
                        assignableId: list.uuid,
                        project,
                      })
                    }
                  />
                </p>
              ),
              endDate: (
                <div className={styles.endDate}>
                  <DateForm
                    list={realList}
                    value={
                      list.endDate
                        ? (DateTime.fromISO(list.endDate, { zone: "utc" })
                            .toUTC()
                            .toISO() ?? "")
                        : ""
                    }
                    projectTimezone={projectTimezone}
                    disabled={isRequestPending}
                    minDate={minDate}
                    maxDate={maxDate}
                    submit={(date) => {
                      const nextDt = DateTime.fromISO(date, { zone: "utc" });
                      const nextIso = nextDt.toUTC().toISO() ?? "";
                      const currentDt = list.endDate
                        ? DateTime.fromISO(list.endDate, { zone: "utc" })
                        : null;
                      editList({
                        list: realList,
                        nextList: {
                          ...realList,
                          endDate:
                            !!currentDt && nextDt.equals(currentDt)
                              ? ""
                              : nextIso,
                        },
                      });
                    }}
                  />
                </div>
              ),
            },
          ),
          comments: (
            <CommentsCount
              subject={list.uuid}
              type="list"
              commentsNbr={meta.commentCount}
            />
          ),
          files: (
            <AttachmentsCount uuid={list.uuid} fileNbr={meta.attachmentCount} />
          ),
          contextualMenu: (
            <div
              ref={ref}
              className={classNames(styles.contextualMenu, {
                [styles.displayedContextualMenu]: showContextualMenu,
              })}
            >
              <ContextualMenu
                list={realList}
                parentList={parentList}
                project={project}
                addIn={addIn}
                addAfter={addAfter}
              />
            </div>
          ),
        })}
      </SecondaryCriteria>
    </div>
  );
}

export { List };
